
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import axiosInstance from "../services/axios";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { Category, FormDto } from "../types/category.types";
import styles from "./css/UploadFormPage.module.css";

interface UploadFormModalProps {
  onFormUploaded?: (newForm: FormDto) => void;
  isAnalyst?: boolean;
  analystId?: number;
  childId?: number;
}

const UploadFormModal: React.FC<UploadFormModalProps> = ({ onFormUploaded, isAnalyst = false, analystId = 0, childId }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  // State for category dialog
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryError, setCategoryError] = useState("");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get<Category[]>("/FormCategory");
        setCategories(response.data);
      } catch (error) {
        setMessage("שגיאה בטעינת הקטגוריות");
      }
    };
    fetchCategories();
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setMessage("");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage("אנא בחר קובץ");
      return;
    }
    if (!selectedCategory || selectedCategory === 0) {
      setMessage("אנא בחר קטגוריה");
      return;
    }
    const selectedCategoryObj = categories.find(c => c.code === selectedCategory);
    if (!selectedCategoryObj || !selectedCategoryObj.categoryName) {
      setMessage("שגיאה: לא נמצאה קטגוריה תקינה. נסה לרענן את הדף או לבחור קטגוריה אחרת.");
      return;
    }
    if (!selectedFile.name) {
      setMessage("שגיאה: שם הקובץ ריק. בחר קובץ אחר.");
      return;
    }

    setIsUploading(true);
    setMessage("");

    try {
      const formData = new FormData();
      formData.append('FormFile', selectedFile);
      formData.append('FileName', selectedFile.name);
      formData.append('FormCategoryCode', selectedCategory.toString());
      formData.append('CategoryName', selectedCategoryObj.categoryName);
      if (analystId) {
        formData.append('AnalystID', analystId.toString());
      }
      if (childId) {
        formData.append('ChildID', childId.toString());
      }
       console.log("Selected file:", selectedFile);
       formData.forEach((value, key) => {
  console.log("FormData:", key, value);
});


      // לוגים להדפסת כל הערכים שנשלחים ב-FormData
      formData.forEach((value, key) => {
        console.log('FormData:', key, value);
      });
      
      const response = await axiosInstance.post<FormDto>("/Form", formData);

      setMessage("הקובץ הועלה בהצלחה!");
      setSelectedFile(null);
      setSelectedCategory(0);
      if (fileInputRef.current) fileInputRef.current.value = "";

      if (onFormUploaded) onFormUploaded(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        setMessage(`שגיאה: ${error.response.data?.message || error.message}`);
      } else {
        setMessage("שגיאה בהעלאה");
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={styles.modalContainer}>
      <h2 className={styles.title}>העלאת טופס חדש</h2>
      <div className={styles.rowWrapper}>
        {/* select ופלוס ימין */}
        <div style={{position:'relative', flex:1, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div className={styles.selectWrapper}>
            <select
              className={styles.select}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(parseInt(e.target.value))}
              disabled={isUploading}
            >
              <option value={0}>בחירת קטגוריה</option>
              {categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.categoryName}
                </option>
              ))}
            </select>
            {/* חץ ורוד */}
            <span className={styles.selectArrow}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 7l5 5 5-5" stroke="#e573b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </div>
          {/* כפתור עגול מוצמד לימין */}
          <button
            className={styles.addCategoryBtn}
            onClick={() => setShowCategoryDialog(true)}
            type="button"
            aria-label="הוספת קטגוריה"
          >
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="#f48fb1" />
              <path d="M16 9v14M9 16h14" stroke="#222" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        {/* uploadBox שמאל */}
        <div className={styles.uploadBox} onClick={() => fileInputRef.current?.click()} style={{cursor:'pointer'}}>
          <div className={styles.uploadIcon}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
              <rect width="60" height="60" rx="12" fill="#f8bbd0"/>
              <path d="M30 40V20M30 20l-8 8M30 20l8 8" stroke="#222" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="18" y="40" width="24" height="3" rx="1.5" fill="#222"/>
            </svg>
          </div>
          <div className={styles.uploadText}>העלאת קובץ</div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className={styles.fileInput}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
          />
          {selectedFile && (
            <div className={styles.fileInfo}>
              {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>
      </div>

      <div className={styles.buttonGroup}>
        <button
          className={styles.confirmBtn}
          onClick={handleUpload}
          disabled={isUploading}
        >
          {isUploading ? "מעלה..." : "אישור"}
        </button>
      </div>

      {message && <p className={styles.message}>{message}</p>}

      {/* דיאלוג להוספת קטגוריה */}
      {showCategoryDialog && (
        <div className={styles.categoryDialogOverlay}>
          <div className={styles.categoryDialog}>
            <h3>הוספת קטגוריה חדשה</h3>
            <input
              type="text"
              value={newCategoryName}
              onChange={e => setNewCategoryName(e.target.value)}
              placeholder="שם הקטגוריה"
              className={styles.categoryInput}
            />
            {categoryError && <div className={styles.error}>{categoryError}</div>}
            <div className={styles.buttonGroup}>
              <button
                onClick={async () => {
                  if (!newCategoryName.trim()) {
                    setCategoryError("יש להזין שם קטגוריה");
                    return;
                  }
                  try {
                    const res = await axiosInstance.post("/FormCategory", { categoryName: newCategoryName });
                    setCategories(prev => [...prev, res.data]);
                    setShowCategoryDialog(false);
                    setNewCategoryName("");
                    setCategoryError("");
                  } catch (err) {
                    setCategoryError("שגיאה בהוספת קטגוריה");
                  }
                }}
                className={styles.confirmBtn}
              >
                הוסף
              </button>
              <button
                onClick={() => {
                  setShowCategoryDialog(false);
                  setNewCategoryName("");
                  setCategoryError("");
                }}
                className={styles.cancelBtn}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadFormModal;
